import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { IvaSettings } from '../models/order.model';
import { AppConfigService } from './app-config.service';

const DEFAULT_IVA_SETTINGS: IvaSettings = {
    enabled: true,
    rate: 0.16,
    activation_date: '2026-01-01 00:00:00'
};

@Injectable({ providedIn: 'root' })
export class SettingsService {
    private http = inject(HttpClient);
    private appConfig = inject(AppConfigService);
    private get baseUrl(): string {
        return this.appConfig.apiUrl;
    }

    // Estado interno
    private ivaSettingsSignal = signal<IvaSettings>(DEFAULT_IVA_SETTINGS);
    private loadingSignal = signal(false);
    private errorSignal = signal<Error | null>(null);
    private loadedSignal = signal(false);

    // Signals públicos
    readonly ivaSettings = computed(() => this.ivaSettingsSignal());
    readonly ivaEnabled = computed(() => this.ivaSettingsSignal().enabled);
    readonly ivaActivationDate = computed(() => this.ivaSettingsSignal().activation_date);
    readonly ivaRate = computed(() => this.ivaSettingsSignal().rate);
    readonly isLoading = computed(() => this.loadingSignal());
    readonly hasError = computed(() => this.errorSignal() !== null);
    readonly error = computed(() => this.errorSignal());

    /**
     * Cargar settings desde la API
     */
    private async loadSettings(): Promise<void> {
        const token = this.getToken();
        if (!token) {
            this.ivaSettingsSignal.set(DEFAULT_IVA_SETTINGS);
            return;
        }

        this.loadingSignal.set(true);
        this.errorSignal.set(null);

        try {
            const response = await fetch(`${this.baseUrl}/settings/iva`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                console.warn('Could not load IVA settings, using defaults');
                this.ivaSettingsSignal.set(DEFAULT_IVA_SETTINGS);
            } else {
                const data = await response.json() as IvaSettings;
                this.ivaSettingsSignal.set(data);
            }
            this.loadedSignal.set(true);
        } catch (error) {
            console.warn('Error loading IVA settings:', error);
            this.ivaSettingsSignal.set(DEFAULT_IVA_SETTINGS);
            this.errorSignal.set(error as Error);
        } finally {
            this.loadingSignal.set(false);
        }
    }

    /**
     * Recargar settings manualmente
     */
    reload(): void {
        if (this.hasValidToken()) {
            this.loadSettings();
        }
    }

    /**
     * Llamar cuando el usuario inicia sesión
     */
    onLogin(): void {
        this.loadSettings();
    }

    /**
     * Llamar cuando el usuario cierra sesión
     */
    onLogout(): void {
        this.ivaSettingsSignal.set(DEFAULT_IVA_SETTINGS);
        this.loadedSignal.set(false);
        this.errorSignal.set(null);
    }

    /**
     * Get IVA settings from API (para compatibilidad)
     */
    getIvaSettings(): Observable<IvaSettings> {
        return this.http.get<IvaSettings>(`${this.baseUrl}/settings/iva`);
    }

    /**
     * Update IVA settings
     */
    updateIvaSettings(settings: Partial<IvaSettings>): Observable<IvaSettings> {
        return this.http.put<IvaSettings>(`${this.baseUrl}/settings/iva`, settings).pipe(
            tap(() => {
                // Recargar después de actualizar
                this.loadSettings();
            })
        );
    }

    /**
     * Check if IVA is currently active based on date
     */
    isIvaActive(): boolean {
        if (!this.ivaEnabled()) return false;
        const activationDate = new Date(this.ivaActivationDate());
        return new Date() >= activationDate;
    }

    private getToken(): string {
        return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || '';
    }

    private hasValidToken(): boolean {
        return !!this.getToken();
    }
}
